import React, { useState } from "react";
import CustomButtonNew from "Common/CustomButtonNew/CustomButtonNew";
import ReactPortal from "Components/ReactPortal/ReactPortal";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import encryption from "Utils/encryptionPassword";
import { toast } from "react-toastify";
import api from "http";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import CustomDraggable from "Common/CustomDraggable/CustomDraggable";
import FullPageLoader from "Components/FullPageLoader/FullPageLoader";
import { BsCheck2Circle } from "react-icons/bs";
import ResetPasswordSuccess from "./ResetPasswordSuccess";
import ResetPasswordStep1 from "./ResetPasswordStep1";
import ResetPasswordStep2 from "./ResetPasswordStep2";
import { getPasswordStrength } from "Utils/passwordStrength";
import PopupFooter from "Components/PopupFooter/PopupFooter";
import { FiShield } from "react-icons/fi";
import Show from "Components/Show/Show";
import { CURRENT_PASSWORD_INCORRECT } from "Common/ToastConstants";

const ResetPasswordPopup = ({ onClose, disableDraggable, email }) => {
  const uniqueId = "ResetPasswordPopup";
  const [step, setStep] = useState(1);
  const [passModel, setPassModel] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const disableButton = getPasswordStrength(passModel.newPassword) !== 4 || passModel.newPassword !== passModel.confirmPassword
  const disableStep1Button = !passModel.currentPassword.trim()
  const [loader, setLoader] = useState(false);
  const { userinfo } = useSelector((state) => state.userAuth);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPassModel((prev) => ({ ...prev, [name]: value }));
  };
  const handleVerify = () => {
    let pass = encryption(passModel.currentPassword);
    if (pass !== userinfo.userPassword) {
      toast.error(CURRENT_PASSWORD_INCORRECT);
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setPassModel((prev) => ({ ...prev, newPassword: "", confirmPassword: "" }));
  };

  const updatePassword = async () => {
    let pass = encryption(passModel.newPassword);
    try {
      setLoader(true);
      const payload = {
        Email: email ? email : userinfo.userEmail,
        NewPassword: pass,
      };
      await api.post(
        `${process.env.REACT_APP_TC_AUTH}/api/Account/ResetPassword`,
        payload
      );
      setStep(3);
    } catch (error) {
      ErrorHandlingMessage(error);
    } finally {
      setLoader(false);
    }
  };
  return (
    <ReactPortal wrapperId={uniqueId}>
      {loader && <FullPageLoader />}
      <div
        data-te-modal-init
        className="fixed inset-0 z-10 flex items-center justify-center bg-gray-500 bg-opacity-75"
        id="exampleModalScrollable"
        tabIndex={-1}
        aria-labelledby="exampleModalScrollableLabel"
        aria-modal="true"
        data-te-open="true"
      >
        <CustomDraggable disableDraggable={disableDraggable} handle={`#${uniqueId}`}>
          <div data-te-modal-dialog-ref className="relative w-[400px] min-[576px]:mx-auto min-[576px]:mt-2 min-[576px]:max-w-[500px] min-[992px]:max-w-[800px] min-[1200px]:max-w-[1140px] flex flex-col overflow-hidden rounded-md bg-white shadow-lg py-2">
            <div
              id={uniqueId}
              className="flex items-center gap-2 p-5 text-2xl font-semibold border-b cursor-grab"
            >
              <span className="p-1 bg-red-100 rounded-md text-primary"><FiShield size={20} /></span>

              {step !== 2 ? "Verify Identity" : "Reset Password"}
            </div>
            {step !== 3 && (
              <div className="flex items-center justify-between p-4 mb-5 text-sm bg-zinc-100">
                <div className="flex items-center gap-2" >
                  {step === 2 ? <BsCheck2Circle className="text-green-600" size={14} /> :
                    <div className={`rounded-full text-xs w-5 h-5 flex items-center justify-center border-2 
                   ${step === 1 ? 'bg-primary text-white border-primary' : 'bg-gray-200 text-gray-600 border-gray-300'}`}>1</div>}
                  <div className={step === 1 ? 'text-primary' : 'text-green-600'}>Verify</div>
                </div>
                <div className={`w-10 h-[1.5px] ${step === 1 ? 'bg-gray-400' : 'bg-green-600'} `}></div>
                <div className="flex items-center gap-2">
                  <div className={`rounded-full text-xs w-5 h-5 flex items-center justify-center border-2 
                  ${step === 2 ? 'bg-primary text-white border-primary' : 'bg-gray-200 text-gray-600 border-gray-300'}`}>2</div>
                  <div className={step === 2 ? 'text-primary' : 'text-gray-600'}>Reset</div>
                </div>
              </div>
            )}
            <div className="px-8 py-4">
              <div className={`overflow-y-auto max-h-[70vh] ${step === 3 ? 'flex flex-col items-center justify-center py-12' : ''}`}>
                <Show when={step === 1}>
                  <ResetPasswordStep1 currentPassword={passModel.currentPassword} onInputChange={handleInputChange} />
                  <PopupFooter onClose={onClose} onConfirm={handleVerify} confirmLabel="Verify and Continue" disabled={disableStep1Button} />
                </Show>
                <Show when={step === 2}>
                  <ResetPasswordStep2 newPassword={passModel.newPassword} confirmPassword={passModel.confirmPassword} onInputChange={handleInputChange} />
                  <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
                    <CustomButtonNew label="Back" variant="outline" onClick={handleBack} />
                    <CustomButtonNew onClick={updatePassword} label="Reset Password" disabled={disableButton} />
                  </div>
                </Show>
                <Show when={step === 3}> <ResetPasswordSuccess onContinue={onClose} /></Show>
              </div>
            </div>
          </div>
        </CustomDraggable>
      </div>
    </ReactPortal>
  );
};

export default ResetPasswordPopup;

ResetPasswordPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  disableDraggable: PropTypes.bool,
  email: PropTypes.string,
};
